import { useEffect, useMemo, useState } from 'react';
import { canShowTerminal, getTerminalInfo } from '../services/TerminalService';

/**
 * Whether the project's Terminal tab is offered to this person (utils/projectTabsModel.js): what the terminal endpoint
 * says (services/TerminalService.js canShowTerminal: the owner or an editor, and the owner's plan has the terminal),
 * the same rule as the Code view's dock. `{ ready, offered, info }`: `ready` once the endpoint answered (or failed,
 * then not offered); no project, nothing asked and not offered.
 *
 * One answer per project shared by every caller (the tab row, the view gate, the tab itself), asked again after a
 * minute. The server refuses the socket anyway when the plan has it off: this only keeps the tab out of the row.
 */
const STALE_MS = 60000;
const cache = new Map();

function load(projectId) {
  const known = cache.get(projectId);
  if (known && Date.now() - known.at < STALE_MS) { return known.promise; }
  const entry = { at: Date.now(), done: false, info: null, promise: null };
  entry.promise = getTerminalInfo(projectId).then((info) => {
    entry.info = info;
    entry.done = true;
    return info;
  });
  cache.set(projectId, entry);
  return entry.promise;
}

/** Forgets every answer (tests). */
export function resetTerminalTabCache() {
  cache.clear();
}

const stateOf = (projectId) => {
  const known = projectId ? cache.get(projectId) : null;
  return known && known.done ? { ready: true, info: known.info } : { ready: !projectId, info: null };
};

export default function useTerminalTab(projectId, enabled = true) {
  const id = enabled ? projectId || null : null;
  const [state, setState] = useState(() => stateOf(id));
  useEffect(() => {
    setState(stateOf(id));
    if (!id) { return undefined; }
    let alive = true;
    load(id).then((info) => { if (alive) { setState({ ready: true, info }); } });
    return () => { alive = false; };
  }, [id]);
  return { ready: state.ready, offered: Boolean(id) && state.ready && canShowTerminal(state.info), info: state.info };
}

/**
 * The tabs a project leaves out for this person: the plan's hidden ones, plus Terminal where it is not offered
 * (`pendingHides`: also while the answer is still coming; false for the view gate, which waits for it).
 */
export function useHiddenWithTerminal(planHidden, projectId, { pendingHides = true } = {}) {
  const { ready, offered } = useTerminalTab(projectId);
  // The gate waits for a project and its answer: a `?tab=terminal` link is not closed while the page loads.
  const without = !offered && (pendingHides || (ready && Boolean(projectId)));
  return useMemo(
    () => (without && !planHidden.includes('terminal') ? [...planHidden, 'terminal'] : planHidden),
    [planHidden, without],
  );
}
